import React, { useEffect, useMemo, useState } from "react";

export default function HeightInputField({
  valueCm,
  onChangeCm,
  disabled = false,
}) {
  const normalizedCm = normalizeHeightValue(valueCm);
  const [mode, setMode] = useState("cm");
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");

  useEffect(() => {
    if (!normalizedCm) {
      setFeet("");
      setInches("");
      return;
    }

    const next = cmToFeetInches(normalizedCm);
    setFeet(String(next.feet));
    setInches(String(next.inches));
  }, [normalizedCm]);

  const imperialLabel = useMemo(() => {
    if (!normalizedCm) return "";
    const next = cmToFeetInches(normalizedCm);
    return `${next.feet} ft ${next.inches} in`;
  }, [normalizedCm]);

  const handleCmChange = (event) => {
    const nextValue = String(event?.target?.value ?? event ?? "").replace(/[^\d]/g, "");
    onChangeCm?.(nextValue);
  };

  const handleFeetChange = (event) => {
    const nextFeet = String(event?.target?.value || "").replace(/[^\d]/g, "");
    setFeet(nextFeet);
    updateFromImperial(nextFeet, inches, onChangeCm);
  };

  const handleInchesChange = (event) => {
    const nextInches = String(event?.target?.value || "").replace(/[^\d]/g, "");
    setInches(nextInches);
    updateFromImperial(feet, nextInches, onChangeCm);
  };

  return (
    <div style={{ ...styles.wrap, ...(disabled ? styles.wrapDisabled : {}) }}>
      {mode === "cm" ? (
        <div style={styles.inlineRow}>
          <input
            type="number"
            min="100"
            max="250"
            step="1"
            style={{ ...styles.input, ...styles.flexInput }}
            value={normalizedCm}
            onChange={handleCmChange}
            placeholder="180"
            disabled={disabled}
          />
          <div style={styles.switchBar}>
            <button
              type="button"
              style={{ ...styles.switchBtn, ...(mode === "cm" ? styles.switchBtnActive : {}) }}
              onClick={() => setMode("cm")}
              disabled={disabled}
            >
              Cm
            </button>
            <button
              type="button"
              style={{ ...styles.switchBtn, ...(mode === "imperial" ? styles.switchBtnActive : {}) }}
              onClick={() => setMode("imperial")}
              disabled={disabled}
            >
              Ft / In
            </button>
          </div>
        </div>
      ) : (
        <div style={styles.imperialInlineRow}>
          <input
            type="number"
            min="1"
            max="8"
            step="1"
            style={styles.input}
            value={feet}
            onChange={handleFeetChange}
            placeholder="Feet"
            disabled={disabled}
            aria-label="Feet"
          />
          <input
            type="number"
            min="0"
            max="11"
            step="1"
            style={styles.input}
            value={inches}
            onChange={handleInchesChange}
            placeholder="Inches"
            disabled={disabled}
            aria-label="Inches"
          />
          <div style={styles.switchBar}>
            <button
              type="button"
              style={{ ...styles.switchBtn, ...(mode === "cm" ? styles.switchBtnActive : {}) }}
              onClick={() => setMode("cm")}
              disabled={disabled}
            >
              Cm
            </button>
            <button
              type="button"
              style={{ ...styles.switchBtn, ...(mode === "imperial" ? styles.switchBtnActive : {}) }}
              onClick={() => setMode("imperial")}
              disabled={disabled}
            >
              Ft / In
            </button>
          </div>
        </div>
      )}
      <div style={styles.helperText}>
        {mode === "cm"
          ? (imperialLabel ? `${imperialLabel}` : "Enter height in centimeters")
          : (normalizedCm ? `${normalizedCm} cm` : "Height in cm will appear here")}
      </div>
    </div>
  );
}

function normalizeHeightValue(value) {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return String(Math.round(parsed));
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function cmToFeetInches(cmValue) {
  const totalInches = Math.round(Number(cmValue) / 2.54);
  return {
    feet: Math.floor(totalInches / 12),
    inches: totalInches % 12,
  };
}

function updateFromImperial(feetValue, inchesValue, onChangeCm) {
  const feet = Number.parseInt(String(feetValue || "").trim(), 10);
  const inches = Number.parseInt(String(inchesValue || "").trim(), 10);

  if (!Number.isFinite(feet)) {
    onChangeCm?.("");
    return;
  }

  const safeFeet = clamp(feet, 1, 8);
  const safeInches = Number.isFinite(inches) ? clamp(inches, 0, 11) : 0;
  const cm = Math.round(((safeFeet * 12) + safeInches) * 2.54);
  onChangeCm?.(String(cm));
}

const styles = {
  wrap: {
    display: "grid",
    gap: 8,
  },
  wrapDisabled: {
    opacity: 0.86,
  },
  inlineRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 10,
    alignItems: "center",
  },
  imperialInlineRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
    gap: 10,
    alignItems: "center",
  },
  switchBar: {
    display: "inline-flex",
    padding: 4,
    borderRadius: 999,
    background: "#fff2ef",
    border: "1px solid rgba(185,52,58,0.14)",
    gap: 4,
    flexShrink: 0,
  },
  switchBtn: {
    minWidth: 86,
    minHeight: 38,
    borderRadius: 999,
    border: "none",
    background: "transparent",
    color: "#6b6f7d",
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: "0.08em",
    cursor: "pointer",
  },
  switchBtnActive: {
    background: "#D73C2E",
    color: "#ffffff",
    boxShadow: "0 8px 18px rgba(185,52,58,0.18)",
  },
  input: {
    width: "100%",
    minHeight: 46,
    borderRadius: 18,
    border: "1px solid rgba(185,52,58,0.26)",
    background: "#ffffff",
    padding: "0 16px",
    fontSize: 16,
    color: "#252935",
    outline: "none",
  },
  flexInput: {
    minWidth: 0,
  },
  helperText: {
    fontSize: 13,
    color: "#6b6f7d",
    paddingLeft: 2,
  },
};
